import {useEffect, useState, useCallback} from 'react';
import {Visit} from '../models/visit.model';
import {VisitAPIDataSource} from '../api/VisitAPIDataSource';
import {mapVisitDTOToModel} from '../mappers/visit.mapper';
import {useVisitsUIStore} from '../store/visitsUIStore';

interface UseStoreVisitsResult {
  lastVisit: Visit | null; // Most recent completed visit
  visitHistory: Visit[]; // All completed visits
  approvedVisits: Visit[]; // Approved visits
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useStoreVisits = (storeId: string | number | undefined): UseStoreVisitsResult => {
  const {
    lastVisit, visitHistory, storeVisitsLoading, storeVisitsError,
    setLastVisit, setVisitHistory, setStoreVisitsLoading, setStoreVisitsError, clearStoreVisits
  } = useVisitsUIStore();

  // Local state for approved visits
  const [approvedVisits, setApprovedVisits] = useState<Visit[]>([]);

  const fetchStoreVisits = useCallback(async () => {
    if (!storeId) {
      clearStoreVisits();
      setApprovedVisits([]);
      return;
    }

    setStoreVisitsLoading(true);
    setStoreVisitsError(null);

    try {
      const historyResponse = await VisitAPIDataSource.getStoreVisitHistory(storeId);
      const allVisits = historyResponse.map(mapVisitDTOToModel);

      const approvedVisitsData = allVisits.filter(visit => visit.status === 'approved');
      setApprovedVisits(approvedVisitsData);
      setVisitHistory(allVisits);

      const sortedHistory = allVisits.sort((a, b) =>
        new Date(b.visitDate).getTime() - new Date(a.visitDate).getTime()
      );
      setLastVisit(sortedHistory[0] || null);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'حدث خطأ في جلب بيانات الزيارات';
      setStoreVisitsError(errorMessage);
    } finally {
      setStoreVisitsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  useEffect(() => {
    fetchStoreVisits();
  }, [fetchStoreVisits]);

  return {
    lastVisit,
    visitHistory,
    approvedVisits,
    loading: storeVisitsLoading,
    error: storeVisitsError,
    refetch: fetchStoreVisits
  };
};
